import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { el, enUS } from 'date-fns/locale'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchUpcomingEvents, type Event } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { EmptyStateBox } from './EmptyStateBox'

export function UpcomingEventsBox() {
  const { t, i18n } = useTranslation(['upcomingPeriods', 'common'])
  const parent = useAuthStore((state) => state.parent)
  const [showEventsModal, setShowEventsModal] = useState(false)

  // Fetch upcoming events only if profile is complete
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['upcoming-events'],
    queryFn: fetchUpcomingEvents,
    enabled: parent?.is_profile_complete ?? false
  })

  // Get the appropriate date locale based on the current language
  const dateLocale = i18n.language === 'el' ? el : enUS

  return (
    <>
      {/* Upcoming Events Card */}
      <div className="card-neobrutalism h-full flex flex-col">
        <div className="flex items-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h2 className="text-2xl font-black text-neobrutalism-text">
            {t('title')}
          </h2>
        </div>
        
        <div className="flex-grow mb-6">
          {eventsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neobrutalism-accent"></div>
            </div>
          ) : events && events.length > 0 ? (
            <div className="space-y-4">
              {events.slice(0, 2).map((event: Event) => (
                <div 
                  key={event.id} 
                  className="border-2 border-yellow-200 rounded-lg bg-yellow-50 p-4 shadow-sm transition-transform hover:translate-y-[-2px]"
                >
                  <div className="flex items-start">
                    <div className="bg-yellow-100 rounded-full p-3 mr-3 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold text-lg text-gray-800">{event.name}</h3>
                      <div className="mt-2 space-y-3 sm:space-y-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                          <div className="flex items-center mb-1 sm:mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm text-gray-700 font-bold">{t('startDate')}:</span>
                          </div>
                          <span className="text-sm text-gray-700 ml-0 sm:ml-1">
                            {format(new Date(event.start_date), 'PPP', { locale: dateLocale })}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                          <div className="flex items-center mb-1 sm:mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm text-gray-700 font-bold">{t('endDate')}:</span>
                          </div>
                          <span className="text-sm text-gray-700 ml-0 sm:ml-1">
                            {format(new Date(event.end_date), 'PPP', { locale: dateLocale })}
                          </span>
                        </div>
                        {event.is_full && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                            <div className="flex items-center mb-1 sm:mb-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span className="text-sm text-red-600 font-bold">{t('waitingList')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyStateBox
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
              text={t('noEvents')}
              bgColor="bg-yellow-50"
              borderColor="border-yellow-200"
              iconBgColor="bg-yellow-100"
              iconColor="text-yellow-600"
            />
          )}
        </div>
        
        {events && events.length > 2 && (
          <button 
            onClick={() => setShowEventsModal(true)}
            className="btn-neobrutalism bg-neobrutalism-accent hover:bg-green-600 block w-full py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base transition-transform hover:translate-y-[-2px]"
          >
            {t('viewAll')}
          </button>
        )}
      </div>

      {/* Events Modal */}
      {showEventsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-neobrutalism max-w-2xl w-full mx-4 relative">
            <button 
              onClick={() => setShowEventsModal(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-neobrutalism-red border-2 border-black rounded-full flex items-center justify-center shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-2xl font-black text-gray-800">
                {t('allEvents')}
              </h2>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {events?.map((event: Event) => (
                <div 
                  key={event.id} 
                  className="border-2 border-yellow-200 rounded-lg p-5 bg-yellow-50 shadow-sm"
                >
                  <div className="flex items-start">
                    <div className="bg-yellow-100 rounded-full p-3 mr-4 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-800">{event.name}</h3>
                      {event.description && (
                        <p className="mt-1 text-gray-700">{event.description}</p>
                      )}
                      <div className="mt-3 space-y-3 sm:space-y-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                          <div className="flex items-center mb-1 sm:mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-gray-700 font-bold">{t('startDate')}:</span>
                          </div>
                          <span className="text-gray-700 ml-0 sm:ml-1">
                            {format(new Date(event.start_date), 'PPP', { locale: dateLocale })}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                          <div className="flex items-center mb-1 sm:mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-700 font-bold">{t('endDate')}:</span>
                          </div>
                          <span className="text-gray-700 ml-0 sm:ml-1">
                            {format(new Date(event.end_date), 'PPP', { locale: dateLocale })}
                          </span>
                        </div>
                        {event.is_full && (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                            <div className="flex items-center mb-1 sm:mb-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span className="text-red-600 font-bold">{t('waitingList')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
} 